/**
 * useSystemSettings Hook - Admin settings (real backend).
 *
 * Backoffice policy:
 * - config shown read-only (env backed)
 * - only safe toggles are writable (maintenance mode)
 */

import { useCallback, useEffect, useState } from "react";
import { API_ENDPOINTS, apiFetchJson } from "@/lib/api";

export interface AdminSystemSettings {
    project_name: string;
    environment: string;
    version: string;
    frontend_url: string;
    backend_url: string;
    discord_guild_id: string;
    founders_roles_ids: string[];
    maintenance_mode: boolean;
    rate_limit_per_minute: number;
    timestamp: string;
}

export interface AdminSecuritySettings {
    jwt_algorithm: string;
    jwt_access_token_expire_minutes: number;
    jwt_refresh_token_expire_days: number;
    cors_origins: string[];
    internal_token_configured: boolean;
    discord_oauth_configured: boolean;
    timestamp: string;
}

export interface AdminGamingSettings {
    zomboid: {
        rcon_host: string;
        rcon_port: number;
        game_port: number;
        rcon_password_configured: boolean;
    };
    timestamp: string;
}

export interface AdminBackupSettings {
    r2: {
        endpoint_configured: boolean;
        bucket_configured: boolean;
        access_key_configured: boolean;
        secret_key_configured: boolean;
    };
    timestamp: string;
}

export interface AdminNotificationsSettings {
    discord_webhook_configured: boolean;
    ksp_discord_webhook_configured: boolean;
    timestamp: string;
}

export interface AdminSettingsResponse {
    system: AdminSystemSettings;
    security: AdminSecuritySettings;
    gaming: AdminGamingSettings;
    backup: AdminBackupSettings;
    notifications: AdminNotificationsSettings;
}

export function useSystemSettings() {
    const [settings, setSettings] = useState<AdminSettingsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const fetchAll = useCallback(async () => {
        try {
            setError(null);
            const data = await apiFetchJson<AdminSettingsResponse>(
                API_ENDPOINTS.ADMIN_SETTINGS_ALL,
            );
            setSettings(data);
        } catch (e) {
            console.error("Failed to fetch admin settings:", e);
            setSettings(null);
            setError(e instanceof Error ? e.message : "Failed to load settings");
        }
    }, []);

    const setMaintenanceMode = useCallback(
        async (enabled: boolean) => {
            setActionLoading("maintenance");
            try {
                await apiFetchJson<any>(API_ENDPOINTS.ADMIN_SETTINGS_MAINTENANCE, {
                    method: "POST",
                    query: { enabled },
                });
                await fetchAll();
                return { success: true as const };
            } catch (e) {
                return {
                    success: false as const,
                    error: e instanceof Error ? e.message : "Maintenance update failed",
                };
            } finally {
                setActionLoading(null);
            }
        },
        [fetchAll],
    );

    const refresh = useCallback(async () => {
        setIsLoading(true);
        await fetchAll();
        setIsLoading(false);
    }, [fetchAll]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return {
        settings,
        isLoading,
        error,
        actionLoading,
        setMaintenanceMode,
        refresh,
    };
}

